import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { menuService } from '@/api/domains/menu/menu.service';
import { extractPermissionsFromMenu } from '@/api/domains/menu/menu.mapper';
import type { LC_ErrorOut } from '@/api/shared/types';
import type { MenuItem } from '@/api/domains/menu/menu.types';
import type { RootState } from '@/store';
import { logout, loginAsync } from './authSlice';
import { setPermissions } from './permissionSlice';


interface MenuState {
  items: MenuItem[];
  configMenu: MenuItem | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  lastFetched: number | null;
}

const initialState: MenuState = {
  items: [],
  configMenu: null,
  status: 'idle',
  error: null,
  lastFetched: null,
};
const FETCH_COOLDOWN_MS = 500;

export const fetchMenuAsync = createAsyncThunk<
  { items: MenuItem[]; configMenu: MenuItem | null },
  void,
  { state: RootState; rejectValue: LC_ErrorOut }
>(
  'menu/fetch',
  async (_, { rejectWithValue, dispatch }) => {
    const response = await menuService.list();
    if (response.success && response.data) {
      // Extract permissions from the raw DTOs before mapping to models
      const permissionsRecord = extractPermissionsFromMenu(response.data);
      dispatch(setPermissions(permissionsRecord));

      const models = response.data.map((dto) => menuService.toModel(dto));
      const { main, config } = menuService.split(models);
      return { items: main, configMenu: config };
    }
    return rejectWithValue(response.error || { description: 'Error al obtener menú' });
  },
  {
    // Prevent duplicate fetches when multiple components dispatch concurrently or 
    // StrictMode double-invokes effects
    condition: (_, { getState }) => {
      const state = getState();
      const menu = state.menu;
      // Block if already loading
      if (menu.status === 'loading') return false;
      // Small cooldown to avoid immediate back-to-back calls (e.g., race before status flips)
      if (menu.lastFetched && Date.now() - menu.lastFetched < FETCH_COOLDOWN_MS) return false;
      return true;
    },
  },
);

// Función utilitaria para resetear el estado del menú
const resetMenuState = (state: MenuState): void => {
  state.items = [];
  state.configMenu = null;
  state.status = 'idle';
  state.error = null;
  state.lastFetched = null;
};

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    clearMenu(state) {
      resetMenuState(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMenuAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchMenuAsync.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.items;
        state.configMenu = action.payload.configMenu;
        state.lastFetched = Date.now();
      })
      .addCase(fetchMenuAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as any)?.description || 'Error al obtener menú';
      })
      // Limpiar menú cuando se cierra sesión o se inicia una nueva sesión
      .addCase(logout, (state) => {
        resetMenuState(state);
      })
      .addCase(loginAsync.fulfilled, (state) => {
        // Al hacer login exitoso, limpiar el menú para forzar un fetch fresco
        resetMenuState(state);
      });
  },
});

export const { clearMenu } = menuSlice.actions;
export default menuSlice.reducer;

// Selectors
const TTL_MS = 10 * 60 * 1000; // 10 minutos

export const selectMenuState = (state: RootState) => state.menu;
export const selectMainMenu = createSelector(selectMenuState, (state) => state.items);
export const selectConfigMenu = createSelector(selectMenuState, (state) => state.configMenu);
export const selectMenuStatus = createSelector(selectMenuState, (state) => state.status);
export const selectMenuError = createSelector(selectMenuState, (state) => state.error);
export const selectIsStale = createSelector(selectMenuState, (state) => {
  if (!state.lastFetched) return true;
  return Date.now() - state.lastFetched > TTL_MS;
});
